<template>
  <div class="teams-receipts-admin-view">
    <div class="container py-4">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h2 class="mb-0">Quittungsverwaltung</h2>
        <div class="d-flex align-items-center gap-2">
          <router-link
            v-if="createReceiptRoute"
            :to="createReceiptRoute"
            class="btn btn-primary d-flex align-items-center"
            title="Neue Quittung erstellen"
          >
            <font-awesome-icon icon="plus" class="me-2" />
            <span>Neue Quittung</span>
          </router-link>
          <router-link
            v-if="selectedTeam && !isProOrUltimate"
            :to="{ name: 'teams-upgrade', params: { id: selectedTeam?.id } }"
            class="btn btn-warning d-flex align-items-center"
            title="Bereich upgraden"
          >
            <font-awesome-icon icon="arrow-up" class="me-2" />
            <span>Bereich upgraden</span>
          </router-link>
          <router-link
            v-else-if="selectedTeam"
            :to="{ name: 'teams-upgrade', params: { id: selectedTeam?.id } }"
            class="btn btn-outline-warning d-flex align-items-center justify-content-center"
            title="Plan verwalten"
          >
            <font-awesome-icon icon="crown" />
          </router-link>
          <router-link
            v-if="selectedTeam"
            :to="{ name: 'teams-settings', params: { id: selectedTeam?.id } }"
            class="btn btn-outline-secondary d-flex align-items-center"
            title="Bereich-Einstellungen"
          >
            <font-awesome-icon icon="cog" class="me-2" />
            <span>Einstellungen</span>
          </router-link>
          <BackButton fallback="teams-list" />
        </div>
      </div>

      <!-- Breadcrumbs -->
      <nav aria-label="breadcrumb" class="mb-4">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
              <font-awesome-icon icon="users" class="me-1" />
              Bereiche
            </router-link>
          </li>
          <li class="breadcrumb-item">
            <span class="text-decoration-none text-muted">
              {{ selectedTeam?.name || 'Bereich' }}
            </span>
          </li>
        </ol>
      </nav>

      <div v-if="selectedTeam" class="team-stats-summary mb-3">
        <div class="d-flex flex-wrap justify-content-between">
          <button
            type="button"
            class="badge bg-secondary d-inline-flex align-items-center p-2 px-4 border-0"
            title="Zur Planverwaltung"
            @click="goToUpgrade"
          >
            <font-awesome-icon icon="info-circle" class="me-1" />
            <span>Status: {{ teamPlanLabel }}</span>
          </button>
          <button
            type="button"
            class="badge bg-info d-inline-flex align-items-center p-2 px-4 border-0"
            title="Zur Mitgliederliste"
            @click="goToMembers"
          >
            <font-awesome-icon icon="users" class="me-1" />
            <span>Mitglieder: {{ numberFormatter.format(teamMemberCount) }}</span>
          </button>
          <button
            type="button"
            class="badge bg-warning text-dark d-inline-flex align-items-center p-2 px-4 border-0"
            title="Zur Mitgliederliste"
            @click="goToMembers"
          >
            <font-awesome-icon icon="user-shield" class="me-1" />
            <span>Administratoren: {{ numberFormatter.format(teamAdminCount) }}</span>
          </button>
          <button
            type="button"
            class="badge bg-light text-dark border d-inline-flex align-items-center p-2 px-4"
            title="Neueste Quittung öffnen"
            @click="goToLatestReceipt"
            :disabled="receipts.length === 0"
          >
            <font-awesome-icon icon="clock" class="me-1" />
            <span>Letzte Quittung: {{ formattedLatestReceiptDate }}</span>
          </button>
          <button
            type="button"
            class="badge bg-success d-inline-flex align-items-center p-2 px-4 border-0"
            title="Zu Transfers"
            @click="goToTransfers"
          >
            <font-awesome-icon icon="coins" class="me-1" />
            <span>Kontostand: {{ formattedTeamReceiptBalance }}</span>
          </button>
          <button
            type="button"
            class="badge bg-primary d-inline-flex align-items-center p-2 px-4 border-0"
            title="Quittungen aktualisieren"
            @click="loadReceipts({ showLoader: false })"
          >
            <font-awesome-icon icon="receipt" class="me-1" />
            <span>Quittungen gesamt: {{ formattedTotalReceipts }}</span>
          </button>
        </div>
      </div>

      <div
        v-if="hasMissingRequiredFields"
        class="alert alert-gradient-warning mb-4"
        role="alert"
      >
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div class="d-flex align-items-center">
            <font-awesome-icon icon="exclamation-triangle" class="me-2" />
            <span>Es fehlen noch erforderliche Angaben. Bitte fülle alle Pflichtfelder aus.</span>
          </div>
          <button type="button" class="btn btn-primary btn-sm" @click="goToFields">
            <font-awesome-icon icon="edit" class="me-1" />
            Felder ausfüllen
          </button>
        </div>
      </div>

      <div v-if="isAdminOrCreator && joinRequests.length > 0" class="card mb-4">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">
            <font-awesome-icon icon="user-clock" class="me-2" />
            Beitrittsanfragen
          </h5>
          <span class="badge bg-primary">{{ joinRequests.length }}</span>
        </div>
        <div class="card-body">
          <div v-if="loadingJoinRequests" class="text-center my-4">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Laden...</span>
            </div>
          </div>
          <div v-else-if="joinRequestsError" class="alert alert-danger" role="alert">
            <font-awesome-icon icon="exclamation-triangle" class="me-2" />
            {{ joinRequestsError }}
          </div>
          <div v-else class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Nutzer</th>
                  <th>E-Mail</th>
                  <th>Einladungscode</th>
                  <th>Angefragt am</th>
                  <th class="text-end">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="request in joinRequests" :key="request.id">
                  <td>{{ request.user?.firstName }} {{ request.user?.lastName }}</td>
                  <td>{{ request.user?.email }}</td>
                  <td>
                    <code>{{ request.invitation?.code }}</code>
                  </td>
                  <td>{{ formatJoinRequestDate(request.requestedAt) }}</td>
                  <td>
                    <div class="d-flex gap-1 justify-content-end">
                      <button
                        class="btn btn-sm btn-info"
                        @click="showUserResponses(request)"
                        title="Antworten anzeigen"
                      >
                        <font-awesome-icon icon="eye" />
                      </button>
                      <button
                        class="btn btn-sm btn-success"
                        @click="approveJoinRequest(request.id)"
                        :disabled="processingJoinRequest === request.id"
                        title="Genehmigen"
                      >
                        <span
                          v-if="processingJoinRequest === request.id"
                          class="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <font-awesome-icon v-else icon="check" />
                      </button>
                      <button
                        class="btn btn-sm btn-danger"
                        @click="handleRejectRequest(request)"
                        title="Ablehnen"
                      >
                        <font-awesome-icon icon="times" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading && !isRefreshing" class="text-center py-5">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Laden...</span>
        </div>
        <p class="mt-2">Lade Quittungen...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="alert alert-danger" role="alert">
        <font-awesome-icon icon="exclamation-triangle" class="me-2" />
        {{ error }}
      </div>

      <!-- Content -->
      <div v-else>
        <div v-if="isRefreshing" class="d-flex align-items-center gap-2 text-muted small mb-3">
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Aktualisiere...</span>
          </div>
          <span>Aktualisiere Liste...</span>
        </div>
        <!-- Filters and Actions -->
        <div class="card mb-4">
          <div class="card-body">
            <div class="row g-3">
              <!-- Search -->
              <div class="col-md-3">
                <label for="search" class="form-label">Suche</label>
                <input
                  id="search"
                  type="text"
                  class="form-control"
                  v-model="filters.search"
                  placeholder="Quittungsnummer, Titel, Benutzer..."
                  @input="debouncedSearch"
                />
              </div>

              <!-- User Filter -->
              <div v-if="isAdminOrCreator" class="col-md-3">
                <label for="userFilter" class="form-label">Benutzer</label>
                <select id="userFilter" class="form-select" v-model="filters.userId">
                  <option
                    v-for="option in userFilterOptions"
                    :key="`user-filter-${option.value || 'all'}`"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <!-- Status Filter -->
              <div class="col-md-2">
                <label for="statusFilter" class="form-label">Status</label>
                <select id="statusFilter" class="form-select" v-model="filters.status">
                  <option value="">Alle</option>
                  <option value="canceled">Storniert</option>
                  <option value="rejected">Abgelehnt</option>
                  <option value="completed">Aktiv</option>
                </select>
              </div>

              <!-- Payment Status Filter -->
              <div v-if="isRefundEnabled" class="col-md-2">
                <label for="payedFilter" class="form-label">Zahlung</label>
                <select id="payedFilter" class="form-select" v-model="filters.payed">
                  <option value="">Alle</option>
                  <option value="open">Offen</option>
                  <option value="planed">Vorbereitet</option>
                  <option value="payed">Bezahlt</option>
                </select>
              </div>

              <!-- Template Filter -->
              <div class="col-md-2">
                <label for="templateFilter" class="form-label">Design</label>
                <select id="templateFilter" class="form-select" v-model="filters.templateId">
                  <option v-for="option in templateFilterOptions" :key="`template-${option.value || 'all'}`" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <!-- Date From -->
              <div class="col-md-2">
                <label for="dateFromFilter" class="form-label">Von Datum</label>
                <input
                  id="dateFromFilter"
                  type="date"
                  class="form-control"
                  v-model="filters.dateFrom"
                />
              </div>

              <!-- Date To -->
              <div class="col-md-2">
                <label for="dateToFilter" class="form-label">Bis Datum</label>
                <input
                  id="dateToFilter"
                  type="date"
                  class="form-control"
                  v-model="filters.dateTo"
                />
              </div>

              <!-- Refund Method Filter -->
              <div v-if="isRefundEnabled" class="col-md-2">
                <label for="refundMethodFilter" class="form-label">Erstattungsart</label>
                <select id="refundMethodFilter" class="form-select" v-model="filters.refundMethod">
                  <option value="">Alle</option>
                  <option value="paypal">PayPal</option>
                  <option value="bank_transfer">Überweisung</option>
                  <option value="cash">Bar</option>
                </select>
              </div>

              <!-- Items per Page -->
              <div class="col-md-2">
                <label for="itemsPerPage" class="form-label">Pro Seite</label>
                <select id="itemsPerPage" class="form-select" v-model="pagination.itemsPerPage">
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="all">Alle</option>
                </select>
              </div>

              <!-- 
              <div class="col-md-2 d-flex align-items-end">
                <button 
                  class="btn btn-outline-primary w-100" 
                  @click="loadReceipts"
                  :disabled="loading"
                >
                  <font-awesome-icon 
                    :icon="loading ? 'spinner' : 'sync'" 
                    :class="{ 'fa-spin': loading }" 
                    class="me-1" 
                  />
                  Aktualisieren
                </button>
              </div>Refresh Button -->
            </div>
          </div>
        </div>



        <!-- Receipts Table (nur auf großen Bildschirmen) -->
        <div class="card d-none d-lg-block">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th class="checkbox-column">
                      <input
                        v-if="selectableReceipts.length > 0"
                        type="checkbox"
                        class="form-check-input"
                        :checked="isAllSelected"
                        @change="toggleSelectAll"
                      />
                      <span v-else class="text-muted">—</span>
                    </th>
                    <th>
                      <button type="button" class="btn p-0 text-decoration-none text-reset fw-semibold border-0 bg-transparent" @click="toggleSort('receiptNumber')">
                        Nummer
                        <span class="ms-1 small text-muted">{{ getSortIndicator('receiptNumber') }}</span>
                      </button>
                    </th>
                    <th>
                      <button type="button" class="btn p-0 text-decoration-none text-reset fw-semibold border-0 bg-transparent" @click="toggleSort('user')">
                        Benutzer
                        <span class="ms-1 small text-muted">{{ getSortIndicator('user') }}</span>
                      </button>
                    </th>
                    <th>
                      <button type="button" class="btn p-0 text-decoration-none text-reset fw-semibold border-0 bg-transparent" @click="toggleSort('amount')">
                        Betrag
                        <span class="ms-1 small text-muted">{{ getSortIndicator('amount') }}</span>
                      </button>
                    </th>
                    <th>
                      <button type="button" class="btn p-0 text-decoration-none text-reset fw-semibold border-0 bg-transparent" @click="toggleSort('status')">
                        Status
                        <span class="ms-1 small text-muted">{{ getSortIndicator('status') }}</span>
                      </button>
                    </th>
                    <th v-if="isRefundEnabled">
                      <button type="button" class="btn p-0 text-decoration-none text-reset fw-semibold border-0 bg-transparent" @click="toggleSort('payed')">
                        Zahlung
                        <span class="ms-1 small text-muted">{{ getSortIndicator('payed') }}</span>
                      </button>
                    </th>
                    <th v-if="isRefundEnabled">
                      <button type="button" class="btn p-0 text-decoration-none text-reset fw-semibold border-0 bg-transparent" @click="toggleSort('refundMethod')">
                        Erstattungsart
                        <span class="ms-1 small text-muted">{{ getSortIndicator('refundMethod') }}</span>
                      </button>
                    </th>
                    <th>
                      <button type="button" class="btn p-0 text-decoration-none text-reset fw-semibold border-0 bg-transparent" @click="toggleSort('resolvedDate')">
                        Datum
                        <span class="ms-1 small text-muted">{{ getSortIndicator('resolvedDate') }}</span>
                      </button>
                    </th>
                    <th width="100">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="receipt in receipts" 
                    :key="receipt.id"
                    class="receipt-row"
                    :class="{ 
                      'selected': selectedReceipts.includes(receipt.id),
                      'not-selectable': !receipt.user
                    }"
                    @click="receipt.user ? toggleReceiptSelection(receipt.id, $event) : null"
                  >
                    <td class="checkbox-column" @click.stop>
                      <input
                        v-if="receipt.user"
                        type="checkbox"
                        class="form-check-input"
                        :value="receipt.id"
                        v-model="selectedReceipts"
                        @click.stop
                      />
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td>
                      <strong>{{ receipt.receiptNumber }}</strong>
                    </td>
                    <td v-if="receipt.user">
                      {{ receipt.user?.firstName }} {{ receipt.user?.lastName }}
                      <br>
                      <small class="text-muted">{{ receipt.user?.email }}</small>
                    </td>
                    <td v-else class="fst-italic ">
                      {{ receipt.userFirstName }} {{ receipt.userLastName }}
                      <br>
                      <small class="text-muted fw-lighter"> -- Nutzer gelöscht --</small>
                    </td>
                    <td>
                      <strong>{{ formatCurrency(receipt.amount) }}</strong>
                    </td>
                    <td>
                      <span :class="getStatusBadgeClass(receipt.status)" class="badge">
                        {{ getStatusLabel(receipt.status) }}
                      </span>
                    </td>
                    <td v-if="isRefundEnabled">
                      <span :class="getPaymentBadgeClass(getReceiptPaymentStatus(receipt))" class="badge">
                        {{ getPaymentLabel(getReceiptPaymentStatus(receipt)) }}
                      </span>
                      <div v-if="receipt.paymentDate" class="small text-muted">
                        {{ formatDate(receipt.paymentDate) }}
                      </div>
                    </td>
                    <td v-if="isRefundEnabled">
                      <span v-if="isInvoiceTemplate(receipt)" class="badge bg-secondary">-</span>
                      <span
                        v-else
                        :class="getRefundMethodBadgeClass(getReceiptRefundMethod(receipt))"
                        class="badge"
                      >
                        {{ getRefundMethodLabel(getReceiptRefundMethod(receipt)) }}
                      </span>
                    </td>
                    <td>
                      <span
                        :title="getReceiptResolvedDateTooltip(receipt) || undefined"
                        class="d-flex flex-column"
                      >
                        {{ formatReceiptResolvedDate(receipt) }}
                        <span
                          v-if="getReceiptResolvedDateSource(receipt)"
                          class="badge bg-light text-muted border mt-1 align-self-start"
                        >
                          {{ getReceiptResolvedDateSource(receipt) }}
                        </span>
                      </span>
                    </td>
                    <td @click.stop>
                      <div class="btn-group btn-group-sm">
                        <button 
                          class="btn btn-outline-primary btn-sm"
                          @click="viewReceipt(receipt)"
                          title="Anzeigen"
                        >
                          <font-awesome-icon icon="eye" />
                        </button>
                        <button 
                          v-if="isRefundEnabled && canEditPaymentStatus(receipt) && getReceiptPaymentStatus(receipt) !== 'payed'"
                          class="btn btn-outline-success btn-sm"
                          @click="updateSinglePaymentStatus(receipt, 'payed')"
                          title="Als bezahlt markieren"
                        >
                          <font-awesome-icon icon="check" />
                        </button>
                        <button 
                          v-if="isRefundEnabled && canEditPaymentStatus(receipt) && getReceiptPaymentStatus(receipt) !== 'open'"
                          class="btn btn-outline-warning btn-sm"
                          @click="updateSinglePaymentStatus(receipt, 'open')"
                          title="Als offen markieren"
                        >
                          <font-awesome-icon icon="clock" />
                        </button>
                        <button 
                          v-if="isRefundEnabled && canEditPaymentStatus(receipt) && getReceiptPaymentStatus(receipt) !== 'planed'"
                          class="btn btn-outline-info btn-sm"
                          @click="updateSinglePaymentStatus(receipt, 'planed')"
                          title="Als vorbereitet markieren"
                        >
                          <font-awesome-icon icon="calendar" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Empty State -->
            <div v-if="receipts.length === 0 && !loading" class="text-center py-5">
              <font-awesome-icon icon="receipt" class="fa-3x text-muted mb-3" />
              <h5>Keine Quittungen gefunden</h5>
              <p class="text-muted">Es wurden keine Quittungen mit den aktuellen Filtern gefunden.</p>
            </div>
          </div>
        </div>

        <!-- Responsive Kartenansicht für kleine Bildschirme -->
        <div class="receipt-cards d-lg-none">
          <div
            v-for="receipt in receipts"
            :key="receipt.id"
            class="receipt-card"
            :class="{ 
              'selected': selectedReceipts.includes(receipt.id),
              'not-selectable': !receipt.user
            }"
            @click="receipt.user ? toggleReceiptSelection(receipt.id, $event) : null"
          >
            <input
              v-if="receipt.user"
              type="checkbox"
              class="receipt-checkbox"
              :checked="selectedReceipts.includes(receipt.id)"
              @change.stop="toggleReceiptSelection(receipt.id, $event)"
            />
            <div class="d-flex justify-content-between align-items-center mb-2">
              <strong>{{ receipt.receiptNumber }}</strong>
              <span :class="getStatusBadgeClass(receipt.status)" class="badge">
                {{ getStatusLabel(receipt.status) }}
              </span>
            </div>
            <div class="mb-1">
              <span class="fw-semibold">Benutzer:</span>
              <span v-if="receipt.user">
                {{ receipt.user?.firstName }} {{ receipt.user?.lastName }}
              </span>
              <span v-else class="fst-italic text-muted">
                {{ receipt.userFirstName }} {{ receipt.userLastName }} -- Nutzer gelöscht --
              </span>
            </div>
            <div class="mb-1">
              <span class="fw-semibold">Betrag:</span>
              {{ formatCurrency(receipt.amount) }}
            </div>
            <div v-if="isRefundEnabled" class="mb-1">
              <span class="fw-semibold">Zahlung:</span>
              <span :class="getPaymentBadgeClass(getReceiptPaymentStatus(receipt))" class="badge">
                {{ getPaymentLabel(getReceiptPaymentStatus(receipt)) }}
              </span>
              <span v-if="receipt.paymentDate" class="small text-muted ms-2">
                {{ formatDate(receipt.paymentDate) }}
              </span>
            </div>
            <div v-if="isRefundEnabled" class="mb-1">
              <span class="fw-semibold">Erstattungsart:</span>
              <span v-if="isInvoiceTemplate(receipt)" class="badge bg-secondary">-</span>
              <span
                v-else
                :class="getRefundMethodBadgeClass(getReceiptRefundMethod(receipt))"
                class="badge"
              >
                {{ getRefundMethodLabel(getReceiptRefundMethod(receipt)) }}
              </span>
            </div>
            <div class="mb-1">
              <span class="fw-semibold">Datum:</span>
              <span :title="getReceiptResolvedDateTooltip(receipt) || undefined" class="d-inline-flex flex-column align-items-start">
                {{ formatReceiptResolvedDate(receipt) }}
                <span
                  v-if="getReceiptResolvedDateSource(receipt)"
                  class="badge bg-light text-muted border mt-1"
                >
                  {{ getReceiptResolvedDateSource(receipt) }}
                </span>
              </span>
            </div>
            <div class="btn-group btn-group-sm mt-2">
              <button class="btn btn-outline-primary btn-sm" @click.stop="viewReceipt(receipt)" title="Anzeigen">
                <font-awesome-icon icon="eye" />
              </button>
              <button v-if="isRefundEnabled && canEditPaymentStatus(receipt) && getReceiptPaymentStatus(receipt) !== 'payed'" class="btn btn-outline-success btn-sm" @click.stop="updateSinglePaymentStatus(receipt, 'payed')" title="Als bezahlt markieren">
                <font-awesome-icon icon="check" />
              </button>
              <button v-if="isRefundEnabled && canEditPaymentStatus(receipt) && getReceiptPaymentStatus(receipt) !== 'open'" class="btn btn-outline-warning btn-sm" @click.stop="updateSinglePaymentStatus(receipt, 'open')" title="Als offen markieren">
                <font-awesome-icon icon="clock" />
              </button>
              <button v-if="isRefundEnabled && canEditPaymentStatus(receipt) && getReceiptPaymentStatus(receipt) !== 'planed'" class="btn btn-outline-info btn-sm" @click.stop="updateSinglePaymentStatus(receipt, 'planed')" title="Als vorbereitet markieren">
                <font-awesome-icon icon="calendar" />
              </button>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.totalPages > 1 && pagination.itemsPerPage !== 'all'" class="d-flex justify-content-between align-items-center mt-4">
          <div class="text-muted">
            Zeige {{ paginationStart + 1 }} bis {{ paginationEnd }} von {{ pagination.totalItems }} Quittungen
          </div>
          <nav>
            <ul class="pagination mb-0">
              <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
                <button class="page-link" @click="changePage(pagination.currentPage - 1)">
                  Zurück
                </button>
              </li>
              
              <li 
                v-for="page in visiblePages" 
                :key="page"
                class="page-item"
                :class="{ active: page === pagination.currentPage }"
              >
                <button class="page-link" @click="changePage(page)">{{ page }}</button>
              </li>
              
              <li class="page-item" :class="{ disabled: pagination.currentPage === pagination.totalPages }">
                <button class="page-link" @click="changePage(pagination.currentPage + 1)">
                  Weiter
                </button>
              </li>
            </ul>
          </nav>
        </div>
        
        <!-- Info für "Alle" Option -->
        <div v-if="pagination.itemsPerPage === 'all'" class="d-flex justify-content-between align-items-center mt-4">
          <div class="text-muted">
            Zeige alle {{ pagination.totalItems }} Quittungen
          </div>
        </div>
      </div>
    </div>

    <!-- Spacer to prevent bulk actions panel from covering pagination -->
    <div v-if="selectedReceipts.length > 0" class="bulk-actions-spacer"></div>

    <!-- Modals werden über notification.js gehandhabt -->

    <!-- Fixed Bulk Actions Panel -->
        <div v-if="selectedReceipts.length > 0" class="bulk-actions-panel">
      <div class="bulk-actions-content">
        <!-- Refund Info Alert -->
        <div
          v-if="showRefundInfoAlert"
          :class="['alert', bulkAlertClass, 'mb-3']"
          role="alert"
        >
          <div class="d-flex align-items-start">
            <font-awesome-icon icon="info-circle" class="me-2 mt-1" />
            <div>
              <strong :class="bulkAlertTitleClass">
                <template v-if="hasInvoiceSelection">Erstattung nicht möglich</template>
                <template v-else>Erstattung möglich</template>
              </strong>
              <div v-if="hasInvoiceSelection" class="mt-1">
                <span class="text-danger fw-semibold">
                  <font-awesome-icon icon="exclamation-triangle" class="me-1" />
                  {{ selectedInvoicesCount }} Rechnung(en) ausgewählt. Rechnungen können nicht erstattet werden.
                </span>
                <div class="small text-muted mt-1">
                  <template v-if="allSelectedAreInvoices">
                    Bitte wähle Quittungen statt Rechnungen aus, um Erstattungen durchzuführen.
                  </template>
                  <template v-else>
                    Entferne Rechnungen aus der Auswahl, um die übrigen {{ selectedRefundableReceiptsCount }} Quittung(en) zu erstatten.
                  </template>
                </div>
              </div>
              <div v-else-if="selectedRefundMethods.length > 1" class="mt-1">
                <span class="text-warning">
                  <font-awesome-icon icon="exclamation-triangle" class="me-1" />
                  Erstattungen sind nur möglich, wenn alle ausgewählten Quittungen die gleiche Erstattungsart haben.
                </span>
                <br>
                <small class="text-muted">
                  Gefundene Erstattungsarten: 
                  <span 
                    v-for="method in selectedRefundMethods" 
                    :key="method" 
                    class="badge bg-secondary me-1 cursor-pointer refund-method-badge"
                    @click="selectByRefundMethod(method)"
                    title="Nur Quittungen mit {{ getRefundMethodLabel(method) }} auswählen"
                  >
                    {{ getRefundMethodLabel(method) }} ({{ refundMethodCounts[method] }})
                  </span>
                </small>
              </div>
              <div v-else-if="selectedRefundMethods.length === 1" class="mt-1">
                <span class="text-success">
                  <font-awesome-icon icon="check-circle" class="me-1" />
                  Alle Quittungen haben die gleiche Erstattungsart: {{ getRefundMethodLabel(selectedRefundMethods[0]) }}
                  <span class="badge bg-success ms-1">{{ refundMethodCounts[selectedRefundMethods[0]] }}</span>
                </span>
              </div>
              <div v-else class="mt-1">
                <span class="text-muted">
                  <font-awesome-icon icon="info-circle" class="me-1" />
                  Keine Erstattungsdaten für die ausgewählten Quittungen gefunden.
                  <span v-if="refundMethodCounts.none" class="badge bg-secondary ms-1">
                    {{ refundMethodCounts.none }} ohne Daten
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center flex-wrap flex-md-nowrap">
          <div>
            <strong>{{ selectedReceipts.length }} Quittung(en) ausgewählt</strong>
          </div>
          <div class="d-flex flex-wrap flex-md-nowrap align-items-center w-100 w-md-auto" style="gap: 0.5rem; justify-content: flex-end;">
            <!-- Zahlungsstatus Dropup -->
            <div v-if="isRefundEnabled && canManagePaymentStatus" class="dropdown dropup">
              <button type="button" class="btn btn-outline-success action-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                <font-awesome-icon icon="check" class="me-1" />
                Zahlungsstatus
              </button>
              <ul class="dropdown-menu">
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="updatePaymentStatus('open')">
                    <font-awesome-icon icon="clock" class="me-2" />
                    Als offen markieren
                  </a>
                </li>
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="updatePaymentStatus('planed')">
                    <font-awesome-icon icon="calendar" class="me-2" />
                    Als vorbereitet markieren
                  </a>
                </li>
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="handlePaymentDateModal">
                    <font-awesome-icon icon="check-circle" class="me-2" />
                    Als bezahlt markieren
                  </a>
                </li>
              </ul>
            </div>
            <!-- Erstattung Button -->
            <button v-if="canShowRefundButtons && commonRefundMethod === 'paypal'" type="button" class="btn btn-outline-primary action-btn" @click="showPayPalModal">
              <font-awesome-icon icon="fab fa-paypal" class="me-1" />
              PayPal
            </button>
            <button v-if="canShowRefundButtons && commonRefundMethod === 'bank_transfer'" type="button" class="btn btn-outline-primary action-btn" @click="showIBANModal">
              <font-awesome-icon icon="money-bill-wave" class="me-1" />
              Überweisung
            </button>
            <button v-if="canShowRefundButtons && commonRefundMethod === 'cash'" type="button" class="btn btn-outline-primary action-btn" @click="showIBANModal">
              <font-awesome-icon icon="money-bill-wave" class="me-1" />
              Bar
            </button>
            <div class="dropdown dropup">
              <button
                type="button"
                class="btn btn-outline-secondary action-btn dropdown-toggle"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                :disabled="!selectedReceipts.length"
              >
                <font-awesome-icon icon="download" class="me-1" />
                Exportieren
              </button>
              <ul class="dropdown-menu dropdown-menu-end">
                <li>
                  <a
                    class="dropdown-item"
                    href="#"
                    @click.prevent="handleExportSelected('excel')"
                  >
                    <font-awesome-icon icon="file-excel" class="me-2" />
                    Als Excel exportieren
                  </a>
                </li>
                <li>
                  <a
                    class="dropdown-item"
                    href="#"
                    @click.prevent="handleExportSelected('zip')"
                  >
                    <font-awesome-icon icon="file-archive" class="me-2" />
                    Als ZIP exportieren
                  </a>
                </li>
              </ul>
            </div>
            <!-- Markierung löschen Button -->
            <button type="button" class="btn btn-outline-secondary action-btn" @click="clearSelection" title="Alle Markierungen löschen">
              <font-awesome-icon icon="times" class="me-1" />
              Markierung löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTeamsStore } from '@/stores/teams'
import { useAuthStore } from '@/stores/auth'
import { receiptsApi } from '@/api/receipts'
import { teamService } from '@/api/teams'
import { showSuccess, showError, showRefundTypeSelectionModal, showExcelModal, showXMLModal, showPaymentDateModal, showPayPalWizardModal, showRejectRequestModal, showUserResponsesModal } from '@/services/notification'
import BackButton from '@/components/common/BackButton.vue'
import { debounce } from 'lodash-es'
import { useTeamIntegrityChecks } from '@/composables/useTeamIntegrityChecks'
import { resolveReceiptDateDetails, getReceiptDateValue } from '@/utils/receiptDateUtils'

const route = useRoute()
const router = useRouter()
const teamsStore = useTeamsStore()
const authStore = useAuthStore()

const numberFormatter = new Intl.NumberFormat('de-DE')

const VALID_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const VALID_PAYMENT_STATUSES = new Set(['open', 'planed', 'payed'])
const VALID_REFUND_METHODS = new Set(['paypal', 'bank_transfer', 'cash'])
const VALID_RECEIPT_STATUSES = new Set([
  'draft',
  'pending',
  'submitted',
  'approved',
  'rejected',
  'completed',
  'canceled'
])
const VALID_SORT_FIELDS = new Set([
  'resolvedDate',
  'receiptNumber',
  'user',
  'amount',
  'status',
  'payed',
  'refundMethod'
])
const DEFAULT_SORT_FIELD = 'resolvedDate'
const DEFAULT_SORT_ORDER = 'desc'

const { selectedTeam } = storeToRefs(teamsStore)

const currentUser = computed(() => authStore.user)
const { loadTeamById, refreshTeams } = teamsStore

const { hasMissingRequiredFields, isAdminOrCreator, checkMissingRequiredFields, checkRefundDataRequirements } = useTeamIntegrityChecks({
  selectedTeam,
  currentUser,
  loadTeamById,
  refreshTeams,
})


const loading = ref(true)
const isRefreshing = ref(false)
let pendingRefreshRequests = 0
const error = ref(null)
const receipts = ref([])
const selectedReceipts = ref([])
const pagination = ref({
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  itemsPerPage: 20
})
const filters = ref({
  search: '',
  userId: '',
  status: '',
  payed: '',
  templateId: '',
  refundMethod: '',
  dateFrom: '',
  dateTo: ''
})
const sort = ref({
  by: DEFAULT_SORT_FIELD,
  order: DEFAULT_SORT_ORDER
})

const templateOptions = ref([])
const templateLabelMap = ref({ '': 'Alle Designs', 'null': 'Ohne Team-Design' })

const userFilterOptions = computed(() => {
  const optionMap = new Map()

  const upsertUserOption = (id, firstName, lastName, email) => {
    if (typeof id !== 'string' || !id.trim()) {
      return
    }

    const trimmedId = id.trim()
    const fullName = `${firstName || ''} ${lastName || ''}`.trim()
    const label = fullName
      ? email
        ? `${fullName} (${email})`
        : fullName
      : email || trimmedId

    if (!optionMap.has(trimmedId)) {
      optionMap.set(trimmedId, { value: trimmedId, label })
    }
  }

  if (Array.isArray(selectedTeam.value?.member)) {
    selectedTeam.value.member.forEach((member) => {
      const user = member?.user || member
      upsertUserOption(
        user?.id || member?.userId || '',
        user?.firstName || member?.firstName || '',
        user?.lastName || member?.lastName || '',
        user?.email || member?.email || ''
      )
    })
  }

  receipts.value.forEach((receipt) => {
    const receiptUser = receipt?.user || {}
    upsertUserOption(
      receiptUser?.id || receipt?.userId || '',
      receiptUser?.firstName || receipt?.userFirstName || '',
      receiptUser?.lastName || receipt?.userLastName || '',
      receiptUser?.email || ''
    )
  })

  const sortedOptions = Array.from(optionMap.values()).sort((a, b) =>
    (a.label || '').localeCompare(b.label || '', 'de', { sensitivity: 'base' })
  )

  const currentUserId = typeof filters.value?.userId === 'string' ? filters.value.userId.trim() : ''
  if (currentUserId && !sortedOptions.some(option => option.value === currentUserId)) {
    sortedOptions.push({ value: currentUserId, label: 'Ausgewählter Benutzer' })
  }

  return [{ value: '', label: 'Alle Benutzer' }, ...sortedOptions]
})

const sanitizeDateValue = (value) => {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  if (!trimmed || trimmed.length !== 10 || !VALID_DATE_PATTERN.test(trimmed)) {
    return null
  }

  const parsed = new Date(`${trimmed}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  try {
    const iso = parsed.toISOString().split('T')[0]
    if (iso !== trimmed) {
      return null
    }
    return VALID_DATE_PATTERN.test(iso) ? iso : null
  } catch (error) {
    return null
  }
}

const buildSanitizedFilters = () => {
  const sanitized = {}
  const raw = filters.value || {}

  if (typeof raw.search === 'string') {
    const trimmed = raw.search.trim()
    if (trimmed) {
      sanitized.search = trimmed
    }
  }

  if (typeof raw.userId === 'string') {
    const trimmed = raw.userId.trim()
    if (trimmed) {
      sanitized.userId = trimmed
    }
  }

  if (typeof raw.status === 'string' && VALID_RECEIPT_STATUSES.has(raw.status)) {
    sanitized.status = raw.status
  }

  if (typeof raw.payed === 'string' && VALID_PAYMENT_STATUSES.has(raw.payed)) {
    sanitized.payed = raw.payed
  }

  if (typeof raw.templateId === 'string' && raw.templateId.trim()) {
    sanitized.templateId = raw.templateId.trim()
  }

  if (typeof raw.refundMethod === 'string' && VALID_REFUND_METHODS.has(raw.refundMethod)) {
    sanitized.refundMethod = raw.refundMethod
  }

  const dateFrom = sanitizeDateValue(raw.dateFrom)
  const dateTo = sanitizeDateValue(raw.dateTo)

  if (dateFrom && dateTo) {
    if (new Date(`${dateFrom}T00:00:00Z`) <= new Date(`${dateTo}T23:59:59Z`)) {
      sanitized.dateFrom = dateFrom
      sanitized.dateTo = dateTo
    } else {
      sanitized.dateFrom = dateFrom
    }
  } else if (dateFrom) {
    sanitized.dateFrom = dateFrom
  } else if (dateTo) {
    sanitized.dateTo = dateTo
  }

  return sanitized
}

const normalizeSortField = (value) => {
  if (typeof value !== 'string') {
    return DEFAULT_SORT_FIELD
  }
  return VALID_SORT_FIELDS.has(value) ? value : DEFAULT_SORT_FIELD
}

const normalizeSortOrder = (value) => {
  if (value === 'asc' || value === 'desc') {
    return value
  }
  return DEFAULT_SORT_ORDER
}

const getSortStartOrder = (field) => {
  if (field === 'resolvedDate' || field === 'amount') {
    return 'desc'
  }
  return 'asc'
}

const getSortIndicator = (field) => {
  if (sort.value.by !== field) {
    return '↕'
  }
  return sort.value.order === 'asc' ? '↑' : '↓'
}

const toggleSort = async (field) => {
  if (!VALID_SORT_FIELDS.has(field)) {
    return
  }

  if (sort.value.by === field) {
    sort.value.order = sort.value.order === 'asc' ? 'desc' : 'asc'
  } else {
    sort.value.by = field
    sort.value.order = getSortStartOrder(field)
  }

  pagination.value.currentPage = 1
  await loadReceipts({ showLoader: false })
}

const computeTemplateLabel = (template) => {
  if (!template) {
    return 'Design'
  }

  if (template.id === null || template.id === undefined) {
    return 'Ohne Team-Design'
  }

  const rawLabel = template.customName || template.label || template.templateDisplayName || template.templateName || 'Design'
  return typeof rawLabel === 'string' ? rawLabel.trim() || 'Design' : 'Design'
}

const buildTemplateGroups = (templates = []) => {
  const groupsByLabel = new Map()

  templates.forEach(template => {
    if (!template) {
      return
    }

    const id = template.id === null || template.id === undefined ? 'null' : String(template.id)
    const label = computeTemplateLabel(template)
    const normalizedKey = typeof label === 'string' ? label.trim().toLowerCase() : 'design'

    if (!groupsByLabel.has(normalizedKey)) {
      groupsByLabel.set(normalizedKey, {
        label: label || 'Design',
        ids: new Set(),
        includeNull: false
      })
    }

    const group = groupsByLabel.get(normalizedKey)
    if (!group.label && label) {
      group.label = label
    }
    if (id === 'null') {
      group.includeNull = true
    } else {
      group.ids.add(id)
    }
  })

  return Array.from(groupsByLabel.values()).map(group => {
    const idList = Array.from(group.ids).sort()
    const values = [...idList]
    if (group.includeNull) {
      values.unshift('null')
    }
    const value = values.join(',') || (group.includeNull ? 'null' : '')

    return {
      label: group.label,
      value,
      ids: idList,
      includeNull: group.includeNull
    }
  }).filter(group => group.value !== '')
}

const templateFilterOptions = computed(() => {
  const templates = Array.isArray(templateOptions.value) ? templateOptions.value : []
  const groups = buildTemplateGroups(templates)
  const sortedGroups = groups.sort((a, b) => (a.label || '').localeCompare(b.label || '', 'de', { sensitivity: 'base' }))

  const options = [
    { value: '', label: 'Alle Designs' }
  ]

  sortedGroups.forEach(group => {
    options.push({ value: group.value, label: group.label })
  })

  const currentValue = filters.value.templateId || ''
  if (currentValue && !options.some(option => option.value === currentValue)) {
    const labels = templateLabelMap.value || {}
    const fallbackLabel = labels[currentValue] || 'Ausgewähltes Design'
    options.push({ value: currentValue, label: fallbackLabel })
  }

  return options
})

const latestReceiptDate = ref(null)

const joinRequests = ref([])
const loadingJoinRequests = ref(false)
const joinRequestsError = ref(null)
const processingJoinRequest = ref(null)

const teamId = computed(() => route.params.id)

const createReceiptRoute = computed(() => {
  if (!selectedTeam.value) return null
  return {
    name: 'receipt-create',
    query: {
      teamId: selectedTeam.value.id,
      teamName: selectedTeam.value.name,
      from: 'team',
      preselectedTeam: 'true'
    }
  }
})

const selectableReceipts = computed(() => {
  return receipts.value.filter(receipt => receipt.user)
})

const isAllSelected = computed(() => {
  return selectableReceipts.value.length > 0 && 
         selectedReceipts.value.length === selectableReceipts.value.length
})

const paginationStart = computed(() => {
  if (pagination.value.itemsPerPage === 'all') {
    return 0
  }
  const itemsPerPage = Number(pagination.value.itemsPerPage) || 1
  const currentPage = Number(pagination.value.currentPage) || 1
  return (currentPage - 1) * itemsPerPage
})

const paginationEnd = computed(() => {
  if (pagination.value.itemsPerPage === 'all') {
    return pagination.value.totalItems
  }
  const itemsPerPage = Number(pagination.value.itemsPerPage) || 1
  return Math.min(paginationStart.value + itemsPerPage, pagination.value.totalItems)
})

const visiblePages = computed(() => {
  const pages = []
  const current = pagination.value.currentPage
  const total = pagination.value.totalPages
  
  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)
  
  if (end - start < 4) {
    if (start === 1) {
      end = Math.min(total, start + 4)
    } else {
      start = Math.max(1, end - 4)
    }
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})


const selectedReceiptsData = computed(() => {
  return receipts.value.filter(receipt => selectedReceipts.value.includes(receipt.id))
})

const selectedInvoicesCount = computed(() => {
  return selectedReceiptsData.value.filter(receipt => isInvoiceTemplate(receipt)).length
})

const hasInvoiceSelection = computed(() => selectedInvoicesCount.value > 0)

const selectedRefundableReceiptsCount = computed(() => {
  return selectedReceiptsData.value.length - selectedInvoicesCount.value
})

const allSelectedAreInvoices = computed(() => {
  return hasInvoiceSelection.value && selectedInvoicesCount.value === selectedReceiptsData.value.length
})

const selectedRefundMethods = computed(() => {
  const methods = new Set()
  
  selectedReceiptsData.value.forEach(receipt => {
    if (isInvoiceTemplate(receipt)) {
      return
    }

    const method = getReceiptRefundMethod(receipt)
    if (method !== 'none') {
      methods.add(method)
    }
  })
  
  return Array.from(methods)
})

const hasSameRefundMethod = computed(() => {
  return selectedRefundMethods.value.length === 1
})

const commonRefundMethod = computed(() => {
  return hasSameRefundMethod.value ? selectedRefundMethods.value[0] : null
})

const refundButtonText = computed(() => {
  if (selectedReceipts.value.length === 0) {
    return 'Erstattung'
  }

  if (hasInvoiceSelection.value) {
    return allSelectedAreInvoices.value ? 'Keine Erstattung für Rechnungen' : 'Rechnungen in Auswahl'
  }
  
  if (selectedRefundMethods.value.length === 0) {
    return 'Keine Erstattungsdaten'
  }
  
  if (hasSameRefundMethod.value) {
    const method = commonRefundMethod.value
    return `Erstattung (${getRefundMethodLabel(method)})`
  }
  
  return 'Unterschiedliche Methoden'
})

const isRefundEnabled = computed(() => selectedTeam.value?.refundSettings?.enabled === true)
const canManagePaymentStatus = computed(() => selectedTeam.value?.role === 'teamAdmin')

const canShowRefundButtons = computed(() => {
  const isProUltimate = isProOrUltimate.value
  
  return selectedReceipts.value.length > 0 && hasSameRefundMethod.value && isProUltimate && isRefundEnabled.value && !hasInvoiceSelection.value
})

const isProOrUltimate = computed(() => {
  const status = selectedTeam.value?.status
  return status === 'pro' || status === 'ultimate'
})

watch(isRefundEnabled, (enabled) => {
  if (!enabled) {
    filters.value.payed = ''
    filters.value.refundMethod = ''
    selectedReceipts.value = []
  }
})

const teamReceiptBalance = computed(() => {
  const amount = selectedTeam.value?.account?.amount
  const numericAmount = Number(amount)
  return Number.isFinite(numericAmount) ? numericAmount : 0
})

const formattedTeamReceiptBalance = computed(() => {
  return numberFormatter.format(teamReceiptBalance.value)
})

const teamMemberCount = computed(() => {
  if (!Array.isArray(selectedTeam.value?.member)) {
    return 0
  }
  return selectedTeam.value.member.length
})

const teamAdminCount = computed(() => {
  if (!Array.isArray(selectedTeam.value?.member)) {
    return 0
  }
  return selectedTeam.value.member.filter((member) => member.role === 'teamAdmin').length
})

const totalReceiptsCount = computed(() => {
  const total = pagination.value?.totalItems
  if (typeof total === 'number' && !Number.isNaN(total)) {
    return total
  }

  if (typeof total === 'string') {
    const parsed = parseInt(total, 10)
    if (!Number.isNaN(parsed)) {
      return parsed
    }
  }

  return receipts.value.length
})

const formattedTotalReceipts = computed(() => {
  return numberFormatter.format(totalReceiptsCount.value)
})

const formattedLatestReceiptDate = computed(() => {
  if (!latestReceiptDate.value) {
    return '—'
  }
  return formatDate(latestReceiptDate.value)
})

const teamPlanLabel = computed(() => {
  const status = selectedTeam.value?.status
  if (!status) {
    return 'Unbekannt'
  }

  const labels = {
    free: 'Free',
    pro: 'Pro',
    ultimate: 'Ultimate'
  }

  return labels[status] || status.charAt(0).toUpperCase() + status.slice(1)
})

const refundButtonDisabledText = computed(() => {
  if (selectedReceipts.value.length === 0) {
    return 'Keine Quittungen ausgewählt'
  }
  
  if (!isProOrUltimate.value) {
    return 'Nur für Pro/Ultimate Teams'
  }

  if (hasInvoiceSelection.value) {
    return allSelectedAreInvoices.value
      ? 'Rechnungen können nicht erstattet werden'
      : 'Entferne Rechnungen aus der Auswahl'
  }
  
  if (!hasSameRefundMethod.value) {
    return 'Unterschiedliche Erstattungsmethoden'
  }
  
  return ''
})

const showRefundInfoAlert = computed(() => {
  return selectedReceipts.value.length > 0 && isProOrUltimate.value && isRefundEnabled.value
})

const bulkAlertClass = computed(() => {
  if (hasInvoiceSelection.value) {
    return 'alert-danger'
  }
  if (selectedRefundMethods.value.length > 1) {
    return 'alert-warning'
  }
  if (selectedRefundMethods.value.length === 1 && selectedRefundableReceiptsCount.value > 0) {
    return 'alert-success'
  }
  return 'alert-info'
})

const bulkAlertTitleClass = computed(() => {
  if (hasInvoiceSelection.value) {
    return 'text-danger fw-semibold'
  }
  if (selectedRefundMethods.value.length > 1) {
    return 'text-warning fw-semibold'
  }
  if (selectedRefundMethods.value.length === 1 && selectedRefundableReceiptsCount.value > 0) {
    return 'text-success fw-semibold'
  }
  return 'text-muted fw-semibold'
})

const refundMethodCounts = computed(() => {
  const counts = {}
  
  selectedReceiptsData.value.forEach(receipt => {
    if (isInvoiceTemplate(receipt)) {
      counts.invoice = (counts.invoice || 0) + 1
      return
    }

    const method = getReceiptRefundMethod(receipt)
    counts[method] = (counts[method] || 0) + 1
  })
  
  return counts
})

const updateTemplateLabels = (templates = []) => {
  if (!Array.isArray(templates)) {
    templateLabelMap.value = { '': 'Alle Designs', 'null': 'Ohne Team-Design' }
    return
  }

  const entries = { '': 'Alle Designs' }
  const groups = buildTemplateGroups(templates)

  if (groups.length === 0) {
    entries['null'] = 'Ohne Team-Design'
  }

  groups.forEach(group => {
    entries[group.value] = group.includeNull && group.value === 'null'
      ? 'Ohne Team-Design'
      : group.label
    group.ids.forEach(id => {
      entries[id] = group.label
    })
    if (group.includeNull) {
      entries['null'] = 'Ohne Team-Design'
    }
  })

  if (!entries['null']) {
    entries['null'] = 'Ohne Team-Design'
  }

  templateLabelMap.value = entries
}


const loadReceipts = async (options = {}) => {
  const { showLoader = true } = options
  const requestedPage = Number(pagination.value.currentPage) || 1
  const requestedItemsPerPage = pagination.value.itemsPerPage

  try {
    if (showLoader) {
      loading.value = true
      isRefreshing.value = false
      pendingRefreshRequests = 0
    } else {
      pendingRefreshRequests += 1
      isRefreshing.value = true
    }
    error.value = null

    let result

    const sanitizedFilters = buildSanitizedFilters()

    if (isAdminOrCreator.value) {
      const params = {
        page: requestedPage,
        limit: requestedItemsPerPage,
        sortBy: normalizeSortField(sort.value.by),
        sortOrder: normalizeSortOrder(sort.value.order),
        ...sanitizedFilters
      }

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key]
        }
      })

      result = await receiptsApi.getTeamReceiptsForAdmin(teamId.value, params)
      receipts.value = Array.isArray(result.receipts) ? result.receipts : []

      const paginationData = result.pagination || {}
      const itemsPerPage = requestedItemsPerPage === 'all'
        ? 'all'
        : Number(paginationData.itemsPerPage ?? paginationData.limit ?? requestedItemsPerPage)

      pagination.value = {
        currentPage: requestedItemsPerPage === 'all' ? 1 : Number(paginationData.currentPage ?? paginationData.page ?? requestedPage),
        totalPages: requestedItemsPerPage === 'all' ? 1 : Number(paginationData.totalPages ?? 1),
        totalItems: Number(paginationData.totalItems ?? paginationData.total ?? receipts.value.length),
        itemsPerPage
      }

      if (requestedItemsPerPage === 'all') {
        pagination.value.itemsPerPage = 'all'
        pagination.value.currentPage = 1
        pagination.value.totalPages = 1
      }
    } else {
      const parsedLimit = requestedItemsPerPage === 'all'
        ? 999999
        : parseInt(requestedItemsPerPage, 10) || 20

      const userParams = {
        page: requestedItemsPerPage === 'all' ? 1 : requestedPage,
        limit: parsedLimit,
        status: sanitizedFilters.status,
        paymentStatus: sanitizedFilters.payed,
        search: sanitizedFilters.search,
        templateId: sanitizedFilters.templateId,
        dateFrom: sanitizedFilters.dateFrom,
        dateTo: sanitizedFilters.dateTo,
        refundMethod: sanitizedFilters.refundMethod,
        viewType: 'own'
      }

      Object.keys(userParams).forEach(key => {
        if (userParams[key] === undefined || userParams[key] === null || userParams[key] === '') {
          delete userParams[key]
        }
      })

      result = await receiptsApi.getTeamReceipts(teamId.value, userParams)
      receipts.value = Array.isArray(result.receipts) ? result.receipts : []

      const paginationData = result.pagination || {}
      const totalItems = Number(paginationData.total ?? paginationData.totalItems ?? receipts.value.length)

      pagination.value = {
        currentPage: requestedItemsPerPage === 'all' ? 1 : Number(paginationData.page || userParams.page || 1),
        totalPages: requestedItemsPerPage === 'all' ? 1 : Number(paginationData.totalPages || 1),
        totalItems,
        itemsPerPage: requestedItemsPerPage === 'all' ? 'all' : Number(paginationData.limit ?? parsedLimit)
      }

      if (requestedItemsPerPage === 'all') {
        pagination.value.itemsPerPage = 'all'
      }
    }

    const templatesFromResponse = Array.isArray(result?.templates) ? result.templates : []
    templateOptions.value = templatesFromResponse
    updateTemplateLabels(templatesFromResponse)

    if (receipts.value.length > 0) {
      if (requestedItemsPerPage === 'all' || requestedPage === 1) {
        latestReceiptDate.value = receipts.value[0].createdAt
      } else if (!latestReceiptDate.value) {
        latestReceiptDate.value = receipts.value[0].createdAt
      }
    } else if (requestedPage === 1) {
      latestReceiptDate.value = null
    }

    selectedReceipts.value = []

  } catch (err) {
    console.error('Fehler beim Laden der Quittungen:', err)
    error.value = err.response?.data?.error || 'Fehler beim Laden der Quittungen'
  } finally {
    if (showLoader) {
      loading.value = false
    } else {
      pendingRefreshRequests = Math.max(0, pendingRefreshRequests - 1)
      if (pendingRefreshRequests === 0) {
        isRefreshing.value = false
      }
    }
  }
}

const debouncedSearch = debounce(() => {
  pagination.value.currentPage = 1
  loadReceipts({ showLoader: false })
}, 300)

const changePage = async (page) => {

  if (pagination.value.itemsPerPage === 'all') {
    return
  }
  
  if (page >= 1 && page <= pagination.value.totalPages) {
    pagination.value.currentPage = page
    await loadReceipts({ showLoader: false })
  }
}

const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedReceipts.value = []
  } else {
    
    selectedReceipts.value = selectableReceipts.value.map(r => r.id)
  }
}

const selectByRefundMethod = (refundMethod) => {


  const receiptsWithMethod = receipts.value.filter(receipt => {
    const method = getReceiptRefundMethod(receipt)
    return method === refundMethod && selectedReceipts.value.includes(receipt.id)
  })
  
  selectedReceipts.value = receiptsWithMethod.map(r => r.id)
  

  const methodLabel = getRefundMethodLabel(refundMethod)
  const count = receiptsWithMethod.length
  
  showSuccess(`${count} Quittung(en) mit ${methodLabel} ausgewählt`)
}

const toggleReceiptSelection = (receiptId, event) => {

  if (event.target.closest('.btn-group') || event.target.closest('a')) {
    return
  }
  
  
  const receipt = receipts.value.find(r => r.id === receiptId)
  if (!receipt || !receipt.user) {
    return 
  }

  const index = selectedReceipts.value.indexOf(receiptId)
  if (index > -1) {

    selectedReceipts.value.splice(index, 1)
  } else {

    selectedReceipts.value.push(receiptId)
  }
}

const clearSelection = () => {
  selectedReceipts.value = []
}

const handleExportSelected = async (type) => {
  if (!selectedReceipts.value.length) {
  showError('Bitte wähle mindestens eine Quittung aus')
    return
  }

  try {
    const exportParams = {
      receiptIds: selectedReceipts.value,
      exportType: type,
    }

    const response = await receiptsApi.exportSelectedReceipts(exportParams)

    const blobType = type === 'zip'
      ? 'application/zip'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

    const fileExtension = type === 'zip' ? 'zip' : 'xlsx'
    const filename = `quittungen-auswahl-${new Date().toISOString().split('T')[0]}.${fileExtension}`

    const blob = new Blob([response], { type: blobType })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)

    showSuccess('Export wurde gestartet')
  } catch (err) {
    console.error('Fehler beim Export der ausgewählten Quittungen:', err)
    showError(err.response?.data?.error || 'Export der ausgewählten Quittungen fehlgeschlagen')
  }
}

const updatePaymentStatus = async (status, paymentDate = null) => {
  if (selectedReceipts.value.length === 0) {
  showError('Bitte wähle mindestens eine Quittung aus')
    return
  }
  
  try {
    await receiptsApi.updateReceiptPaymentStatus(selectedReceipts.value, status, paymentDate)
    showSuccess(`${selectedReceipts.value.length} Quittung(en) erfolgreich aktualisiert`)
    selectedReceipts.value = []
    loadReceipts()
  } catch (err) {
    console.error('Fehler beim Aktualisieren des Zahlungsstatus:', err)
    showError(err.response?.data?.error || 'Fehler beim Aktualisieren des Zahlungsstatus')
  }
}

const updateSinglePaymentStatus = async (receipt, status) => {
  try {
    let paymentDate = null
    
    if (status === 'payed') {
      paymentDate = await showPaymentDateModal()
      if (!paymentDate) {
        return 
      }
    }
    
    await receiptsApi.updateReceiptPaymentStatus([receipt.id], status, paymentDate)
    showSuccess('Quittung erfolgreich aktualisiert')
    loadReceipts()
  } catch (err) {
    console.error('Fehler beim Aktualisieren des Zahlungsstatus:', err)
    showError(err.response?.data?.error || 'Fehler beim Aktualisieren des Zahlungsstatus')
  }
}

const handlePaymentDateModal = async () => {
  const paymentDate = await showPaymentDateModal()
  
  if (paymentDate) {
    await updatePaymentStatus('payed', paymentDate)
  }
}

const showPayPalModal = async () => {
  if (selectedReceipts.value.length === 0) {
  showError('Bitte wähle mindestens eine Quittung aus')
    return
  }
  

  const refundType = await showRefundTypeSelectionModal(selectedReceipts.value.length, 'paypal')
  
  if (refundType === 'paypal_wizard') {
    await handlePayPalWizard()
  } else if (refundType === 'paypal_excel') {
    await handlePayPalExcel()
  }
}

const handlePayPalWizard = async () => {
  await showPayPalWizardModal(
    selectedReceipts.value,
    (result) => {

      showSuccess(`PayPal Wizard abgeschlossen: ${result.completedPayments} von ${result.totalUsers} Nutzern bearbeitet`)
      selectedReceipts.value = []
      loadReceipts()
    },
    (error) => {

      console.error('Fehler im PayPal Wizard:', error)
    }
  )
}

const handlePayPalExcel = async () => {
  const result = await showExcelModal(selectedReceipts.value.length)
  if (result) {
    await generatePayPalFile(result.paymentStatus, result.groupByUser)
  }
}

const generatePayPalFile = async (paymentStatus = 'planed', groupByUser = true) => {
  try {
    const payload = {
      receiptIds: selectedReceipts.value,
      refundType: 'paypal_excel',
      groupByUser: groupByUser
    }
    
    const result = await receiptsApi.generateRefundFiles(payload)
    

    const blob = new Blob([result], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `paypal-erstattungen-${new Date().toISOString().split('T')[0]}.xlsx`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
    
    showSuccess('PayPal Excel-Datei erfolgreich generiert und heruntergeladen')
    

    if (paymentStatus !== 'none') {
      try {
        const paymentDate = paymentStatus === 'payed' ? new Date().toISOString().split('T')[0] : null
        await receiptsApi.updateReceiptPaymentStatus(selectedReceipts.value, paymentStatus, paymentDate)
        showSuccess(`Zahlungsstatus auf "${getPaymentLabel(paymentStatus)}" aktualisiert`)
      } catch (error) {
        console.error('Fehler beim Aktualisieren des Zahlungsstatus:', error)
        showError('Datei generiert, aber Fehler beim Aktualisieren des Zahlungsstatus')
      }
    }
    
    loadReceipts()
  } catch (error) {
    console.error('Fehler beim Generieren der PayPal Excel-Datei:', error)
    showError('Fehler beim Generieren der PayPal Excel-Datei: ' + error.message)
  }
}

const showIBANModal = async () => {
  if (selectedReceipts.value.length === 0) {
  showError('Bitte wähle mindestens eine Quittung aus')
    return
  }
  

  const refundMethod = commonRefundMethod.value
  
  if (refundMethod === 'bank_transfer') {

    const refundType = await showRefundTypeSelectionModal(selectedReceipts.value.length, 'bank_transfer')
    
    if (refundType === 'excel') {
      await handleExcelModal()
    } else if (refundType === 'xml') {
      await handleXMLModal()
    } else if (refundType === 'zip') {
      await handleZIPModal()
    }
  } else if (refundMethod === 'cash') {
    await handleCashModal()
  } else {
    showError('Ungültige Erstattungsmethode')
  }
}

const handleExcelModal = async () => {
  const result = await showExcelModal(selectedReceipts.value.length)
  if (result) {
    await generateIBANFile('iban_excel', result.paymentStatus, null, null, result.groupByUser)
  }
}

const handleXMLModal = async () => {
  const formData = await showXMLModal(selectedReceipts.value.length, teamId.value)
  if (formData) {
    await generateIBANFile('sepa_zip', formData.paymentStatus, formData.sepaLibVersion, {
      debtorName: formData.debtorName,
      debtorIBAN: formData.debtorIBAN,
      debtorBIC: formData.debtorBIC
    }, formData.groupByUser)
  }
}

const handleZIPModal = async () => {
  const formData = await showXMLModal(selectedReceipts.value.length, teamId.value)
  if (formData) {
    await generateIBANFile('sepa_zip', formData.paymentStatus, formData.sepaLibVersion, {
      debtorName: formData.debtorName,
      debtorIBAN: formData.debtorIBAN,
      debtorBIC: formData.debtorBIC
    }, formData.groupByUser)
  }
}

const handleCashModal = async () => {
  const refundType = await showRefundTypeSelectionModal(selectedReceipts.value.length, 'cash')
  if (refundType === 'cash_excel') {
    const result = await showExcelModal(selectedReceipts.value.length)
    if (result) {
      await generateCashFile(result.paymentStatus, result.groupByUser)
    }
  }
}

const generateIBANFile = async (refundType, paymentStatus = 'planed', sepaLibVersion = 'DE-3.7', debtorData = null, groupByUser = true) => {
  try {
    if (refundType === 'iban_xml') {

      const payload = {
        receiptIds: selectedReceipts.value,
        refundType: 'iban_xml',
        sepaLibVersion: sepaLibVersion,
        groupByUser: groupByUser
      }
      

      if (debtorData) {
        payload.debtorName = debtorData.debtorName
        payload.debtorIBAN = debtorData.debtorIBAN
        payload.debtorBIC = debtorData.debtorBIC
      }
      
      const result = await receiptsApi.generateRefundFiles(payload)
      

      const blob = new Blob([result], { type: 'application/xml' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sepa-ueberweisung-${new Date().toISOString().split('T')[0]}.xml`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      showSuccess('XML-Datei erfolgreich generiert und heruntergeladen')
    } else if (refundType === 'sepa_zip') {

      const payload = {
        receiptIds: selectedReceipts.value,
        refundType: 'sepa_zip',
        sepaLibVersion: sepaLibVersion,
        groupByUser: groupByUser
      }
      

      if (debtorData) {
        payload.debtorName = debtorData.debtorName
        payload.debtorIBAN = debtorData.debtorIBAN
        payload.debtorBIC = debtorData.debtorBIC
      }
      
      const result = await receiptsApi.generateRefundFiles(payload)
      

      const blob = new Blob([result], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sepa-ueberweisung-${new Date().toISOString().split('T')[0]}.zip`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      showSuccess('ZIP-Datei mit XML und Excel erfolgreich generiert und heruntergeladen')
    } else {

      const result = await receiptsApi.generateRefundFiles({
        receiptIds: selectedReceipts.value,
        refundType: 'iban_excel',
        groupByUser: groupByUser
      })
      

      const blob = new Blob([result], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `iban-erstattungen-${new Date().toISOString().split('T')[0]}.xlsx`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      showSuccess('Excel-Datei erfolgreich generiert und heruntergeladen')
    }
    

    if (paymentStatus !== 'none') {
      try {
        const paymentDate = paymentStatus === 'payed' ? new Date().toISOString().split('T')[0] : null
        await receiptsApi.updateReceiptPaymentStatus(selectedReceipts.value, paymentStatus, paymentDate)
        showSuccess(`Zahlungsstatus auf "${getPaymentLabel(paymentStatus)}" aktualisiert`)
      } catch (error) {
        console.error('Fehler beim Aktualisieren des Zahlungsstatus:', error)
        showError('Datei generiert, aber Fehler beim Aktualisieren des Zahlungsstatus')
      }
    }
    
    loadReceipts()
  } catch (error) {
    console.error('Fehler beim Generieren der Datei:', error)
    showError(error.message || 'Die Datei konnte nicht generiert werden. Bitte versuche es erneut.')
  }
}

const generateCashFile = async (paymentStatus = 'planed', groupByUser = true) => {
  try {
          const result = await receiptsApi.generateRefundFiles({
        receiptIds: selectedReceipts.value,
        refundType: 'cash_excel',
        groupByUser: groupByUser
      })

    const blob = new Blob([result], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `bar-erstattungen-${new Date().toISOString().split('T')[0]}.xlsx`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
    
    showSuccess('Bar-Erstattungs-Excel-Datei erfolgreich generiert und heruntergeladen')

    if (paymentStatus !== 'none') {
      try {
        const paymentDate = paymentStatus === 'payed' ? new Date().toISOString().split('T')[0] : null
        await receiptsApi.updateReceiptPaymentStatus(selectedReceipts.value, paymentStatus, paymentDate)
        showSuccess(`Zahlungsstatus auf "${getPaymentLabel(paymentStatus)}" aktualisiert`)
      } catch (error) {
        console.error('Fehler beim Aktualisieren des Zahlungsstatus:', error)
        showError('Datei generiert, aber Fehler beim Aktualisieren des Zahlungsstatus')
      }
    }
    
    selectedReceipts.value = []
    loadReceipts()
  } catch (error) {
    console.error('Fehler beim Generieren der Bar-Erstattungs-Datei:', error)
  showError('Fehler beim Generieren der Datei. Bitte versuch es erneut.')
  }
}



const goToFields = () => {
  if (!selectedTeam.value) return
  router.push({ name: 'teams-fields', params: { id: selectedTeam.value.id } })
}

const goToMembers = () => {
  if (!selectedTeam.value) return
  router.push({ name: 'teams-members', params: { id: selectedTeam.value.id } })
}

const goToUpgrade = () => {
  if (!selectedTeam.value) return
  router.push({ name: 'teams-upgrade', params: { id: selectedTeam.value.id } })
}

const goToTransfers = () => {
  if (!selectedTeam.value) return
  router.push({ name: 'teams-transfers', params: { id: selectedTeam.value.id } })
}

const goToLatestReceipt = () => {
  if (!Array.isArray(receipts.value) || receipts.value.length === 0) {
    return
  }
  viewReceipt(receipts.value[0])
}

const formatJoinRequestDate = (dateString) => {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const showUserResponses = (request) => {
  const fields = request.team?.fields || []
  const responses = request.userFieldResponses || {}
  const userName = `${request.user?.firstName ?? ''} ${request.user?.lastName ?? ''}`.trim()

  showUserResponsesModal(userName || request.user?.email || 'Unbekannter Benutzer', fields, responses)
}

const loadJoinRequests = async () => {
  if (!selectedTeam.value?.id || !isAdminOrCreator.value) {
    joinRequests.value = []
    return
  }

  try {
    loadingJoinRequests.value = true
    joinRequestsError.value = null

    const response = await teamService.getTeamJoinRequests(selectedTeam.value.id, 'pending')
    joinRequests.value = response.requests || []
  } catch (err) {
    console.error('Fehler beim Laden der Beitrittsanfragen:', err)

    if (err.response?.status === 403 || err.response?.status === 401) {
      joinRequests.value = []
      joinRequestsError.value = 'Du hast keine Berechtigung zum Anzeigen der Beitrittsanfragen.'
      showError('Du hast keine Berechtigung mehr für diesen Bereich')
      router.push({ name: 'teams-list' })
      return
    }

    joinRequestsError.value = 'Beitrittsanfragen konnten nicht geladen werden'
  } finally {
    loadingJoinRequests.value = false
  }
}

const approveJoinRequest = async (requestId) => {
  try {
    processingJoinRequest.value = requestId
    await teamService.approveJoinRequest(requestId)
    showSuccess('Beitrittsanfrage wurde genehmigt')

    joinRequests.value = joinRequests.value.filter((req) => req.id !== requestId)

    await loadTeamById(selectedTeam.value.id)
    await refreshTeams()
    await loadJoinRequests()
  } catch (err) {
    console.error('Fehler beim Genehmigen der Anfrage:', err)
    showError('Beitrittsanfrage konnte nicht genehmigt werden')
  } finally {
    processingJoinRequest.value = null
  }
}

const rejectJoinRequest = async (requestId, reason = '') => {
  try {
    await teamService.rejectJoinRequest(requestId, reason)
    showSuccess('Beitrittsanfrage wurde abgelehnt')

    joinRequests.value = joinRequests.value.filter((req) => req.id !== requestId)

    await loadTeamById(selectedTeam.value.id)
    await refreshTeams()
    await loadJoinRequests()
  } catch (err) {
    console.error('Fehler beim Ablehnen der Anfrage:', err)
    showError('Beitrittsanfrage konnte nicht abgelehnt werden')
  }
}

const handleRejectRequest = (request) => {
  const userName = `${request.user?.firstName ?? ''} ${request.user?.lastName ?? ''}`.trim()

  showRejectRequestModal(userName || request.user?.email || 'Unbekannter Benutzer', async (reason) => {
    await rejectJoinRequest(request.id, reason)
  })
}

const viewReceipt = (receipt) => {
  const filterKey = `teams-receipts-filters-${teamId.value}`
  sessionStorage.setItem(filterKey, JSON.stringify({
    filters: filters.value,
    sort: sort.value,
    currentPage: pagination.value.currentPage,
    itemsPerPage: pagination.value.itemsPerPage,
  }))
  router.push({ name: 'receipt-detail', params: { id: receipt.id } })
}


const formatCurrency = (amount) => {
  return new Intl.NumberFormat('de-DE', { 
    style: 'currency', 
    currency: 'EUR' 
  }).format(amount)
}

const formatDate = (dateString) => {
  if (!dateString) {
    return '-'
  }
  const parsed = new Date(dateString)
  if (Number.isNaN(parsed.getTime())) {
    return '-'
  }
  return parsed.toLocaleDateString('de-DE')
}

const formatReceiptResolvedDate = (receipt) => {
  const value = getReceiptDateValue(receipt)
  if (!value) {
    return '-'
  }
  return formatDate(value)
}

const getReceiptResolvedDateTooltip = (receipt) => {
  const details = resolveReceiptDateDetails(receipt)
  return details.tooltip || undefined
}

const getReceiptResolvedDateSource = (receipt) => {
  const details = resolveReceiptDateDetails(receipt)
  return details.source === 'field' ? details.sourceLabel : null
}

const getStatusBadgeClass = (status) => {
  const classes = {
    draft: 'bg-secondary',
    pending: 'bg-warning',
    submitted: 'bg-info',
    approved: 'bg-success',
    rejected: 'bg-danger',
    completed: 'bg-primary',
    canceled: 'bg-danger'
  }
  return classes[status] || 'bg-secondary'
}

const getStatusLabel = (status) => {
  const labels = {
    draft: 'Entwurf',
    pending: 'Ausstehend',
    submitted: 'Eingereicht',
    approved: 'Genehmigt',
    rejected: 'Abgelehnt',
    completed: 'Aktiv',
    canceled: 'Storniert'
  }
  return labels[status] || status
}

const TERMINAL_RECEIPT_STATUSES = new Set(['rejected', 'canceled'])

const hasTerminalReceiptStatus = (receipt) => {
  return TERMINAL_RECEIPT_STATUSES.has(receipt?.status)
}

const getReceiptPaymentStatus = (receipt) => {
  if (hasTerminalReceiptStatus(receipt)) {
    return null
  }

  const payed = receipt?.payed
  if (typeof payed === 'string' && VALID_PAYMENT_STATUSES.has(payed)) {
    return payed
  }

  return null
}

const canEditPaymentStatus = (receipt) => {
  return canManagePaymentStatus.value && !hasTerminalReceiptStatus(receipt)
}

const getPaymentBadgeClass = (payed) => {
  const classes = {
    open: 'bg-warning',
    planed: 'bg-info',
    payed: 'bg-success'
  }
  return classes[payed] || 'bg-secondary'
}

const getPaymentLabel = (payed) => {
  const labels = {
    open: 'Offen',
    planed: 'Vorbereitet',
    payed: 'Bezahlt'
  }
  if (typeof payed !== 'string' || !payed.trim()) {
    return '-'
  }
  return labels[payed] || '-'
}

const getRefundMethodBadgeClass = (refundMethod) => {
  const classes = {
    paypal: 'bg-primary',
    bank_transfer: 'bg-success',
    cash: 'bg-warning',
    none: 'bg-secondary'
  }
  return classes[refundMethod] || 'bg-secondary'
}

const isInvoiceTemplate = (receipt) => {
  if (!receipt) {
    return false
  }

  const normalize = (value) =>
    typeof value === 'string' ? value.trim().toLowerCase() : ''

  const documentType = normalize(receipt.documentType)
  if (documentType === 'invoice') {
    return true
  }

  const template = receipt.template
  const nestedTemplate = template?.template
  const templateName =
    normalize(nestedTemplate?.name) ||
    normalize(template?.name) ||
    normalize(template?.settings?.templateName) ||
    normalize(receipt.templateName)
  if (templateName === 'invoice') {
    return true
  }

  const templateType =
    normalize(nestedTemplate?.type) ||
    normalize(template?.type) ||
    normalize(template?.settings?.type) ||
    normalize(receipt.templateType)

  return templateType === 'invoice'
}

const getReceiptRefundMethod = (receipt) => {
  const allowedMethods = new Set(['paypal', 'bank_transfer', 'cash', 'none'])

  if (hasTerminalReceiptStatus(receipt)) {
    return 'none'
  }

  if (isInvoiceTemplate(receipt)) {
    return 'none'
  }

  if (typeof receipt?.refundMethod === 'string' && allowedMethods.has(receipt.refundMethod)) {
    return receipt.refundMethod
  }
  
  if (receipt.user && receipt.user.refundData && receipt.user.refundData.length > 0) {
    const refundMethod = receipt.user.refundData[0].refundMethod
    return allowedMethods.has(refundMethod) ? refundMethod : 'none'
  }
  
  return 'none'
}

const getRefundMethodLabel = (refundMethod) => {
  const labels = {
    paypal: 'PayPal',
    bank_transfer: 'Überweisung',
    cash: 'Bar',
    none: '-'
  }
  return labels[refundMethod] || '-'
}


watch(
  () => selectedTeam.value?.id,
  async () => {
    await loadJoinRequests()
  }
)

watch(
  () => isAdminOrCreator.value,
  async (isAdmin) => {
    if (isAdmin) {
      await loadJoinRequests()
    } else {
      joinRequests.value = []
    }
  }
)

watch(() => filters.value.status, async () => {
  pagination.value.currentPage = 1
  await loadReceipts({ showLoader: false })
})

watch(() => filters.value.userId, async () => {
  pagination.value.currentPage = 1
  await loadReceipts({ showLoader: false })
})

watch(() => filters.value.payed, async () => {
  pagination.value.currentPage = 1
  await loadReceipts({ showLoader: false })
})

watch(() => filters.value.templateId, async () => {
  pagination.value.currentPage = 1
  await loadReceipts({ showLoader: false })
})

watch(() => filters.value.dateFrom, async (newValue) => {
  pagination.value.currentPage = 1
  if (newValue && !sanitizeDateValue(newValue)) {
    return
  }
  await loadReceipts({ showLoader: false })
})

watch(() => filters.value.dateTo, async (newValue) => {
  pagination.value.currentPage = 1
  if (newValue && !sanitizeDateValue(newValue)) {
    return
  }
  await loadReceipts({ showLoader: false })
})

watch(() => filters.value.refundMethod, async () => {
  pagination.value.currentPage = 1
  await loadReceipts({ showLoader: false })
})

watch(() => pagination.value.itemsPerPage, async () => {
  pagination.value.currentPage = 1
  await loadReceipts({ showLoader: false })
})


onMounted(async () => {
  try {
    const routeUserId =
      typeof route.query?.userId === 'string' ? route.query.userId.trim() : ''

    const filterKey = `teams-receipts-filters-${teamId.value}`
    const saved = sessionStorage.getItem(filterKey)
    if (saved) {
      const { filters: savedFilters, sort: savedSort, currentPage, itemsPerPage } = JSON.parse(saved)
      filters.value = { ...filters.value, ...savedFilters }
      if (savedSort && typeof savedSort === 'object') {
        sort.value.by = normalizeSortField(savedSort.by)
        sort.value.order = normalizeSortOrder(savedSort.order)
      }
      pagination.value.currentPage = currentPage ?? 1
      pagination.value.itemsPerPage = itemsPerPage ?? 20
      sessionStorage.removeItem(filterKey)
    }

    if (routeUserId) {
      filters.value = {
        ...filters.value,
        search: '',
        status: '',
        payed: '',
        templateId: '',
        refundMethod: '',
        dateFrom: '',
        dateTo: '',
        userId: routeUserId,
      }
      pagination.value.currentPage = 1
    }

    if (!selectedTeam.value || selectedTeam.value.id !== teamId.value) {
      await loadTeamById(teamId.value)
    }


    await Promise.all([loadReceipts(), loadJoinRequests()])
    await checkMissingRequiredFields()
    await checkRefundDataRequirements()
    

  } catch (err) {
    console.error('Fehler beim Laden der Daten:', err)
    error.value = 'Fehler beim Laden der Daten'
  }
})
</script>
